import { useOktaAuth } from '@okta/okta-react';
import { isAxiosError } from 'axios';
import { createContext, ReactNode, useContext, useEffect } from 'react';

import { useCurrentUserEmail } from '@/components/hooks/useCurrentUserEmail';
import { useFeaturePermissionsQuery } from '@/components/shared/api/queries/permissions';
import { isServerOutageError } from '@/components/shared/api/retry';
import { Feature } from '@/components/shared/constants/features';
import { APIRoutes } from '@/components/shared/routes';
import {
    EVENT_LOG_NAMES,
    getLoggingContext,
    honeybadgerLogEvent,
} from '@/components/shared/utils/logging';
import { getAccessToken } from '@/components/shared/utils/sessionStorageUtils';

import { PermissionDeniedDialogProvider } from './PermissionDeniedDialogProvider';
import { ServerOutageDialog } from './ServerOutageDialog';

interface PermissionsContextValue {
    hasFeature: (feature: Feature) => boolean;
    isLoading: boolean;
    isError: boolean;
    isServerOutage: boolean;
}

// Fail closed: deny all features by default when used outside provider
const PermissionsContext = createContext<PermissionsContextValue>({
    hasFeature: () => false,
    isLoading: true,
    isError: false,
    isServerOutage: false,
});

export const PermissionsContextProvider = ({ children }: { children: ReactNode }) => {
    const accessToken = getAccessToken();
    const { authState } = useOktaAuth();
    const currentUserEmail = useCurrentUserEmail();
    const { data: features, isLoading, isError, error } = useFeaturePermissionsQuery(!!accessToken);
    const isServerOutage = isError && isServerOutageError(error);

    useEffect(() => {
        if (isError) {
            console.error('Failed to load feature permissions.', error);

            honeybadgerLogEvent(EVENT_LOG_NAMES.FeaturePermissionsLoadError, {
                level: 'ERROR',
                customAttributes: {
                    ...getLoggingContext(
                        currentUserEmail,
                        authState?.idToken?.claims?.sub,
                    ),
                    errorCode: isAxiosError(error)
                        ? (error.response?.status ?? error.code ?? 'unknown')
                        : 'unknown',
                    errorMessage: error instanceof Error ? error.message : String(error),
                    route: APIRoutes.PERMISSIONS_FEATURES,
                    serverOutage: isServerOutage,
                },
            });
        }
    }, [isError, error, isServerOutage, currentUserEmail, authState]);

    const hasFeature = (feature: Feature): boolean => {
        if (isLoading || !features || (isError && !isServerOutage)) return false;

        return features.includes(feature);
    };

    return (
        <PermissionsContext.Provider value={{ hasFeature, isLoading, isError, isServerOutage }}>
            <PermissionDeniedDialogProvider>{children}</PermissionDeniedDialogProvider>
            <ServerOutageDialog visible={isServerOutage} />
        </PermissionsContext.Provider>
    );
};

export const usePermissions = () => useContext(PermissionsContext);
